import type { AxiosError } from "axios";
import { toast } from "sonner";
import type { StateCreator } from "zustand";

import { ATTRIBUTE_ENTITY } from "~/core/constants/attributes";
import type { BackendApiErrorResponse } from "~/core/types/backend-api-response.type";

import { deleteCompanyById } from "~/lib/database/company-data";
import { updateOrgObjectAttributeOrder } from "~/lib/database/object-data";

import { defaultCompanyQuery } from "../constants";
import type { CompanyDatabaseRow } from "../types/common.type";
import type { ZCompanyState } from "../types/zustand.type";

const whitelistParamsForBrowserURL = ["q", "sort_by"];

export const companyDatabaseSlice: StateCreator<ZCompanyState> = (
  set,
  get,
) => ({
  rows: [],
  fetchedPages: {},
  gridColumns: [],
  prospectList: [],
  isProspecting: false,
  columns: [],
  removeColumnByProperty: property_name => {
    set(state => ({
      columns: state.columns.filter(a => a.property_name !== property_name),
    }));
  },

  isOpenAddCompanyModal: false,
  setIsOpenAddCompanyModal: isOpen => {
    set({ isOpenAddCompanyModal: isOpen });
  },

  isFetching: false,
  setIsFetching: isFetching => {
    set({ isFetching });
  },

  pagination: {
    limit: 20,
    page: 1,
    total: 0,
  },

  accountObjectViews: [],
  setAccountObjectViews: accountObjectViews => {
    set({ accountObjectViews });
  },
  showableAccountObjectViews: [],
  setShowableAccountObjectViews: showableAccountObjectViews => {
    set({ showableAccountObjectViews });
  },

  isFilterPanelShown: false,
  setIsFilterPanelShown: isFilterPanelShown => {
    set({ isFilterPanelShown });
  },
  activeView: null,
  setActiveView: view => {
    set({ activeView: view });
  },
  sortings: [],
  setSortings: sortings => {
    set({ sortings });
  },
  isSaveButtonShown: false,
  setIsSaveButtonShown: isSaveButtonShown => {
    set({ isSaveButtonShown });
  },

  selectedRowIds: [],
  setSelectedRowIds: (rowIds: string[]) => {
    set({ selectedRowIds: rowIds });
  },
  accountFilters: [],
  setAccountFilters: filters => {
    set({ accountFilters: filters });
  },

  removeAccounts: (rowIds, gridApiRef) => {
    const gridRows = gridApiRef.current?.getRowModels();
    rowIds.forEach(rowId => {
      gridRows.delete(rowId);
    });

    const entries = Array.from(gridRows.entries()) as [
      string,
      CompanyDatabaseRow,
    ][];
    const updatedRow = entries.map(([id, data]) => ({
      ...(data as CompanyDatabaseRow),
      id,
    })) as CompanyDatabaseRow[];

    set({ rows: updatedRow });
  },
  query: defaultCompanyQuery,
  setQuery: queryParams => {
    const searchParams = new URLSearchParams(window.location.search);
    set(state => {
      const updatedQueryParams = { ...state.query, ...queryParams };
      Object.entries(updatedQueryParams).forEach(([key, value]) => {
        if (whitelistParamsForBrowserURL.includes(key) && value) {
          searchParams.set(key, value.toString());
        } else {
          searchParams.delete(key);
        }
      });
      return {
        query: updatedQueryParams,
      };
    });
    const queryParamsStr = searchParams.toString();
    const newUrl = queryParamsStr
      ? `${window.location.pathname}?${queryParamsStr}`
      : window.location.pathname;
    window.history.pushState({}, "", newUrl);
  },

  moveColToLeft: async (colField: string, accessToken: string) => {
    const { columns } = get();
    const colIndex = columns.findIndex(col => col.property_name === colField);
    const col = columns[colIndex];
    const gridCol = get().gridColumns[colIndex];

    if (colIndex < 1) return; // can't move left anymore

    const toBeNextCol = columns[colIndex - 1];
    col.attribute_order = col.attribute_order - 1;
    toBeNextCol.attribute_order = toBeNextCol.attribute_order + 1;

    const updatedRawCols = [
      ...columns.slice(0, colIndex - 1),
      col,
      columns[colIndex - 1],
      ...columns.slice(colIndex + 1),
    ];

    set(state => ({
      columns: updatedRawCols,
      gridColumns: [
        ...state.gridColumns.slice(0, colIndex - 1),
        gridCol,
        state.gridColumns[colIndex - 1],
        ...state.gridColumns.slice(colIndex + 1),
      ],
    }));

    try {
      await updateOrgObjectAttributeOrder({
        object_type: ATTRIBUTE_ENTITY.COMPANY,
        attributes: updatedRawCols.map(col => ({
          id: col.attribute_id,
          order: col.attribute_order,
        })),
      });
    } catch (error) {
      const err = error as AxiosError<BackendApiErrorResponse>;
      if (err.response) {
        toast.error(err.response.data.message ?? "Something went wrong");
      }
    }
  },

  moveColToRight: async (colField: string, accessToken: string) => {
    const { columns } = get();
    const colIndex = columns.findIndex(col => col.property_name === colField);
    const col = columns[colIndex];
    const gridCol = get().gridColumns[colIndex];

    if (colIndex === columns.length - 1) return; // can't move right anymore

    const toBeLeftCol = columns[colIndex + 1];
    col.attribute_order = col.attribute_order + 1;
    toBeLeftCol.attribute_order = toBeLeftCol.attribute_order - 1;

    const updatedRawCols = [
      ...columns.slice(0, colIndex),
      columns[colIndex + 1],
      col,
      ...columns.slice(colIndex + 2),
    ];

    set(state => ({
      columns: updatedRawCols,
      gridColumns: [
        ...state.gridColumns.slice(0, colIndex),
        state.gridColumns[colIndex + 1],
        gridCol,
        ...state.gridColumns.slice(colIndex + 2),
      ],
    }));

    try {
      await updateOrgObjectAttributeOrder({
        object_type: ATTRIBUTE_ENTITY.COMPANY,
        attributes: updatedRawCols.map(col => ({
          id: col.attribute_id,
          order: col.attribute_order,
        })),
      });
    } catch (error) {
      const err = error as AxiosError<BackendApiErrorResponse>;
      if (err.response) {
        toast.error(err.response.data.message ?? "Something went wrong");
      }
    }
  },

  deleteCompany: async (companyId, accessToken) => {
    set(state => ({
      rows: state.rows.filter(row => row.id !== companyId),
    }));

    try {
      await deleteCompanyById(companyId, accessToken);
    } catch (error) {
      const err = error as AxiosError<BackendApiErrorResponse>;
      if (err.response) {
        toast.error(err.response.data.message ?? "Something went wrong");
      }
    }
  },

  activeDetailsPanelTab: "emails",
  setActiveDetailsPanelTab: tab => {
    set({ activeDetailsPanelTab: tab });
  },

  hoveredRowId: null,
  setHoveredRowId: rowId => {
    set({ hoveredRowId: rowId });
  },

  rowSelectionModel: "",
  setRowSelectionModel: rowId => {
    set({ rowSelectionModel: rowId });
  },

  detailPanelEmails: [],
  setDetailPanelEmails: emails => {
    set({ detailPanelEmails: emails });
  },

  detailPanelNotes: [],
  setDetailPanelNotes: notes => {
    set({ detailPanelNotes: notes });
  },

  lastSelectedCell: null,
  setLastSelectedCell: cell => {
    set({ lastSelectedCell: cell });
  },

  activeAccountData: null,
  setActiveAccountData: account => {
    set({ activeAccountData: account });
  },

  detailPanelContacts: [],
  setDetailPanelContacts: contacts => {
    set({ detailPanelContacts: contacts });
  },

  searchViewText: "",
  setSearchViewText: text => {
    set({ searchViewText: text });
  },

  isDeleteAllAccounts: false,
  setIsDeleteAllAccounts: isDeleteAll => {
    set({ isDeleteAllAccounts: isDeleteAll });
  },

  pushSelectedRowIds: rowIds => {
    set(state => ({
      selectedRowIds: [...state.selectedRowIds, ...rowIds],
    }));
  },
});
